<template>
  <lila-default-template>
    <template #mainContent>
      <div class="container licenses-page">
        <!-- Alternância interna: licenças de Pacientes x Profissionais -->
        <lila-license-tabs v-model="activeTab" />

        <!-- Cards separados por tipo de licença -->
        <patient-licenses-card v-if="activeTab === 'patients'" />
        <professional-licenses-card v-else />
      </div>
    </template>
  </lila-default-template>
</template>

<script>
import DefaultTemplate from "@components/template/DefaultTemplate.vue";
import LicenseTabs from "@/views/management/LicenseTabs.vue";
import PatientLicensesCard from "@/views/management/PatientLicensesCard.vue";
import ProfessionalLicensesCard from "@/views/management/ProfessionalLicensesCard.vue";

export default {
  components: {
    "lila-default-template": DefaultTemplate,
    "lila-license-tabs": LicenseTabs,
    "patient-licenses-card": PatientLicensesCard,
    "professional-licenses-card": ProfessionalLicensesCard
  },
  data() {
    return {
      activeTab: "patients" // 'patients' | 'professionals'
    };
  }
};
</script>

<!-- Estilos compartilhados pelos dois cards de licença (globais, sem scoped). -->
<style lang="scss">
$lila-primary: #4f3f82;
$lila-accent: #9a397d;

.licenses-page {
  padding-top: 10px;
}

.licenses-page h1.title {
  color: $lila-primary;
  font-weight: 700;
  margin-bottom: 16px;
}

/* Resumo de licenças (mesmos valores do PatientsManagement / dashboard:
   texto 16px/500/#444 e badge colorido conforme a disponibilidade). */
.licenses-page .licenses-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #444444;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 32px;
}

.licenses-page .licenses-dot {
  color: #c7c7c7;
}

.licenses-page .licenses-contact {
  color: $lila-accent;
  font-weight: 700;
  text-decoration: none;
}

.licenses-page .licenses-contact:hover {
  text-decoration: underline;
}

/* Filtros */
.licenses-page .licenses-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 32px;
}

.licenses-page .licenses-filter {
  flex: 0 1 320px;
  min-width: 240px;
}

.licenses-page .licenses-filter--search {
  flex: 0 1 420px;
}

/* Filtro de Equipe: botão-gatilho + popover do picker (padrão do dropdown) */
.licenses-page .licenses-filter-picker-wrap {
  position: relative;
}

.licenses-page .licenses-filter-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  background: #ffffff;
  border: none;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(79, 63, 130, 0.1);
  padding: 11px 20px;
  font-size: 14px;
  color: #3a3a3a;
  cursor: pointer;
  font-family: inherit;
}

.licenses-page .licenses-filter-select > span:first-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.licenses-page .licenses-filter-select .is-placeholder {
  color: #9a95a8;
}

.licenses-page .licenses-filter-caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid $lila-accent;
  flex: none;
}

.licenses-page .licenses-filter-select.has-clear {
  padding-right: 48px;
}

.licenses-page .licenses-filter-clear {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0 0 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: $lila-accent;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

.licenses-page .licenses-filter-clear:hover {
  opacity: 0.7;
}

.licenses-page .licenses-filter-picker {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 600;
}

.licenses-page .licenses-filter-label {
  display: block;
  font-size: 13px;
  color: #6b6b6b;
  margin-bottom: 8px;
}

/* Lista em cards: cada linha é um card independente com GAP entre si */
.licenses-page .licenses-table-wrapper {
  overflow-x: auto;
}

.licenses-page .licenses-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 14px; /* espaçamento vertical entre os cards */
}

.licenses-page .licenses-table thead th {
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #3a3a3a;
  padding: 4px 16px 10px;
  white-space: nowrap;
  border: none; /* remove a linha inferior herdada do Bulma (.content table th) */
}

/* Cada linha vira um card branco separado */
.licenses-page .licenses-table tbody tr {
  background: #ffffff;
  box-shadow: 0 1px 5px rgba(79, 63, 130, 0.07);
  transition: box-shadow 0.2s ease;
}

.licenses-page .licenses-table tbody tr:hover {
  box-shadow: 0 3px 12px rgba(79, 63, 130, 0.12);
}

.licenses-page .licenses-table tbody td {
  padding: 18px 16px;
  font-size: 14px;
  color: #8a8a8a;
  background: transparent;
  white-space: nowrap;
}

/* Arredonda as pontas do card (primeira/última célula) */
.licenses-page .licenses-table tbody td:first-child {
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.licenses-page .licenses-table tbody td:last-child {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
}

.licenses-page .licenses-table .licenses-name {
  color: #3a3a3a;
  font-weight: 400;
}

/* Toggle "Licenciado" */
.licenses-page .licenses-switch {
  display: inline-block;
  height: 22px;
  width: 45px;
  position: relative;
}

.licenses-page .licenses-switch input {
  display: none;
}

.licenses-page .licenses-slider {
  background-color: #e0e0e0;
  border-radius: 34px;
  bottom: 0;
  cursor: pointer;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  transition: 0.4s;
}

.licenses-page .licenses-slider:before {
  background-color: #ffffff;
  border-radius: 50%;
  bottom: 3px;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  position: absolute;
  transition: 0.4s;
}

.licenses-page .licenses-switch input:checked + .licenses-slider {
  background-color: $lila-primary;
}

.licenses-page .licenses-switch input:checked + .licenses-slider:before {
  transform: translateX(21px);
}

.licenses-page .licenses-switch input:disabled + .licenses-slider {
  opacity: 0.6;
  cursor: default;
}

/* Botão editar */
.licenses-page .licenses-edit-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  line-height: 0;
}

.licenses-page .licenses-edit-btn svg,
.licenses-page .licenses-edit-btn img {
  width: 18px;
  height: 18px;
}

/* O SvgIcon tem @click próprio com stopPropagation; se o clique cair em
   qualquer descendente (div wrapper, <svg>, <path>...) ele é engolido antes
   de chegar no <button>, exigindo um segundo clique. Neutralizar TUDO dentro
   do botão pra que só o próprio <button> receba o evento. */
.licenses-page .licenses-edit-btn * {
  pointer-events: none;
}

.licenses-page .licenses-empty {
  padding: 30px 0;
  color: #9a9a9a;
}

/* Coluna "Ativo" (status do profissional) */
.licenses-page .licenses-status {
  font-weight: 600;
}

.licenses-page .licenses-status.is-active {
  color: #3a3a3a;
}

.licenses-page .licenses-status.is-inactive {
  color: #b0b0b0;
}

/* Coluna de seleção (bulk-edit) */
.licenses-page .licenses-check-col {
  width: 44px;
}

.licenses-page .licenses-check {
  display: inline-flex;
  cursor: pointer;
}

.licenses-page .licenses-check input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: $lila-primary;
}

.licenses-page .licenses-table tbody tr.licenses-row--selected td {
  background-color: rgba(79, 63, 130, 0.06);
}

/* Barra de ação em lote (bulk-edit) */
.licenses-page .licenses-bulk-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding: 12px 16px;
  background-color: #f4f2fa;
  border: 1px solid #e2ddf0;
  border-radius: 10px;
}

.licenses-page .licenses-bulk-count {
  font-weight: 600;
  color: $lila-primary;
}

.licenses-page .licenses-bulk-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.licenses-page .licenses-bulk-apply {
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  background-color: $lila-primary;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.licenses-page .licenses-bulk-apply:hover:not(:disabled) {
  opacity: 0.9;
}

.licenses-page .licenses-bulk-apply:disabled {
  opacity: 0.5;
  cursor: default;
}

.licenses-page .licenses-bulk-apply--outline {
  background-color: transparent;
  color: $lila-primary;
  border: 1px solid $lila-primary;
}

.licenses-page .licenses-bulk-clear {
  border: none;
  background: transparent;
  color: #6b6b6b;
  cursor: pointer;
  text-decoration: underline;
  margin-left: auto;
}
</style>
